/**
 * One public legal document (Terms of Service, Privacy Policy or Refund Policy)
 * at its own URL (/terms, /privacy, /refund). Reachable signed out, and linked
 * from the site footer on every page. The English text lives in
 * `legal/documents.tsx`, translations in `legal/translations/<lang>.tsx` (loaded
 * on demand); this page is only the frame around them. A translation is marked
 * as such, with a switch to the governing English version.
 */
import { useEffect, useState, type MouseEvent } from "react";
import { buildLegalDocument, LEGAL_TRANSLATIONS, type LegalDocumentId } from "../legal/documents.js";
import type { LegalTexts } from "../legal/shared.js";
import { LEGAL_EFFECTIVE_DATE, siteInfo, type SiteInfo } from "../lib/siteInfo.js";
import { parseRoute } from "../lib/route.js";
import { useLanguage, useT, type TranslationKey } from "../lib/i18n/store.js";
import type { Page } from "../types.js";

const DOCUMENT_LABEL_KEYS: Record<LegalDocumentId, TranslationKey> = {
  terms: "footer.terms",
  privacy: "footer.privacy",
  refund: "footer.refund"
};

export function LegalDocumentPage({
  documentId,
  onNavigate,
  site = siteInfo
}: {
  documentId: LegalDocumentId;
  onNavigate: (page: Page) => void;
  /** Injectable for tests; defaults to the build-time site info. */
  site?: SiteInfo;
}) {
  const t = useT();
  const lang = useLanguage();
  // The last translation chunk loaded (texts null = it failed to load), and the
  // language the reader switched to English in — both keyed by language, so a
  // language change never shows a stale translation or a stale "English" choice.
  const [translation, setTranslation] = useState<{ lang: string; texts: LegalTexts | null } | null>(null);
  const [englishChosenFor, setEnglishChosenFor] = useState<string | null>(null);
  const hasTranslation = Boolean(LEGAL_TRANSLATIONS[lang]);

  useEffect(() => {
    const load = LEGAL_TRANSLATIONS[lang];
    if (!load) return;
    let active = true;
    load().then(
      (module) => active && setTranslation({ lang, texts: module.texts }),
      () => active && setTranslation({ lang, texts: null }) // stay on English if the chunk cannot load
    );
    return () => {
      active = false;
    };
  }, [lang]);

  const loaded = translation && translation.lang === lang ? translation : null;
  // The reader's language has a translation that is still downloading: show a
  // placeholder rather than the English text it would replace a moment later.
  // (A chunk that fails to load settles on English, so this never sticks.)
  const translationPending = hasTranslation && !loaded;
  const translationAvailable = Boolean(loaded?.texts);
  const texts = loaded?.texts && englishChosenFor !== lang ? loaded.texts : undefined;
  const isTranslation = Boolean(texts);
  // While the translation chunk is still loading, claim neither "translation" nor "English only".
  const notice = translationAvailable
    ? t("legal.translationNotice")
    : !hasTranslation || loaded
      ? t("legal.englishOnly")
      : null;
  const doc = buildLegalDocument(documentId, site, texts);
  const effectiveDate = new Date(`${LEGAL_EFFECTIVE_DATE}T00:00:00Z`).toLocaleDateString(lang, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC"
  });

  // Links inside the documents are plain same-origin hrefs (/terms, /pricing…)
  // so they also work when copied; route them in-app instead of reloading.
  function handleLinkClick(event: MouseEvent<HTMLElement>): void {
    const anchor = (event.target as HTMLElement).closest("a");
    const href = anchor?.getAttribute("href");
    if (!href || !href.startsWith("/") || event.metaKey || event.ctrlKey) return;
    const route = parseRoute(href);
    if (route.page === "landing" && href !== "/") return;
    event.preventDefault();
    onNavigate(route.page);
  }

  const related = (Object.keys(DOCUMENT_LABEL_KEYS) as LegalDocumentId[]).filter((id) => id !== documentId);

  if (translationPending) {
    return (
      <section className="legal-page" aria-busy="true">
        <p className="sr-only" role="status">{t("detail.loading")}</p>
        <article className="legal-card legal-card--loading" aria-hidden="true">
          <div className="skeleton skeleton-line skeleton-line--title" />
          <div className="skeleton skeleton-line skeleton-line--short" />
          <div className="skeleton skeleton-line" />
          <div className="skeleton skeleton-line" />
          <div className="skeleton skeleton-line" />
          <div className="skeleton skeleton-line skeleton-line--short" />
        </article>
      </section>
    );
  }

  return (
    <section className="legal-page">
      <article className="legal-card" lang={isTranslation ? lang : "en"} onClick={handleLinkClick}>
        <h1>{doc.title}</h1>
        <p className="legal-meta">
          {t("legal.effective", { date: effectiveDate })}
          {notice && <> · {notice}</>}{" "}
          {translationAvailable && (
            <button
              type="button"
              className="auth-inline-link"
              onClick={() => setEnglishChosenFor(isTranslation ? lang : null)}
            >
              {isTranslation ? t("legal.showEnglish") : t("legal.showTranslation")}
            </button>
          )}
        </p>
        <p className="legal-intro">{doc.summary}</p>

        {doc.sections.map((section) => (
          <section key={section.heading} className="legal-section">
            <h2>{section.heading}</h2>
            {section.body}
          </section>
        ))}

        <nav className="legal-related" aria-label={t("legal.otherDocs")}>
          <span>{t("legal.otherDocs")}:</span>
          {related.map((id) => (
            <button key={id} type="button" className="auth-inline-link" onClick={() => onNavigate(id)}>
              {t(DOCUMENT_LABEL_KEYS[id])}
            </button>
          ))}
        </nav>
      </article>
    </section>
  );
}
